<template>
  <header class="trip-details-header">
    <div class="trip-details-header__bg">
      <img :src="trip.img" />
    </div>
    <div class="trip-details-header__shadow" />
    <div class="trip-details-header__close" @click="$emit('close')">X</div>
    <div class="trip-details-header__content">
      <div class="trip-details-header__title">
        <h6>{{ trip.theme }}</h6>
        <h4>{{ trip.name }}</h4>
      </div>
      <div class="trip-details-header__actions">
        <button @click="remove">Remove Trip</button>
        <button class="trip-details-header__save download-btn" :class="{loading: isPDFLoading}" @click="onDownloadPdf">
          <SpinnerIcon class="icon" v-if="isPDFLoading" />
          <DownloadIcon class="icon" v-else />
          <span>Download as PDF</span>
        </button>
        <button class="trip-details-header__save" @click="edit">Edit Trip</button>
      </div>
    </div>
  </header>
</template>

<script>
import {getUserId} from '@/services/auth';
import {removeTrip} from '@/store/api/remove-trip';
import {dropSolution, fetchMyTrips, updateDestName, updateSolutionImg} from '@/store/state/helpers';
import {RouterAliases} from '@/router/routerAliases';
import {useRouter} from '@/router';
import {useSaveSolutionDialog} from '../summary-page/useSaveSolutionDialod';

import Vue from 'vue';
import VueCompositionAPI, {defineComponent} from '@vue/composition-api';
import SpinnerIcon from '../svg/spinner-icon.vue';
import DownloadIcon from '../svg/download-icon.vue';

Vue.use(VueCompositionAPI);

export default defineComponent({
  name: 'AmexMyTripsDetailsHeader',
  components: {
    SpinnerIcon,
    DownloadIcon,
  },
  props: {
    trip: {
      type: Object,
      required: true,
    },
  },
  setup(props, {emit, root}) {
    const {isPDFLoading, onDownloadPdf: getPdf} = useSaveSolutionDialog(root);
    const remove = async () => {
      await removeTrip(props.trip.tripId, getUserId());
      emit('close');
      await fetchMyTrips();
    };

    const edit = async () => {
      await dropSolution();
      const img = props.trip.img;
      const name = props.trip.name;
      await updateSolutionImg(img);
      await updateDestName(name);
      const router = useRouter();
      router.push({name: RouterAliases.SUMMARY_VIEW, params: {tripId: props.trip.tripId, contextName: name}});
    };

    const onDownloadPdf = () => {
      getPdf(props.trip.tripId, props.trip.name);
    };

    return {
      remove,
      edit,
      onDownloadPdf,
      isPDFLoading,
    };
  },
});
</script>

<style lang="scss" scoped>
.trip-details-header {
  $block: &;

  width: 100%;
  height: 450px;
  position: relative;

  &__bg {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  &__shadow {
    position: absolute;
    width: 100%;
    height: 50%;
    bottom: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.55) 100%);
    transition: 0.3s ease transform;
  }

  &__close {
    position: absolute;
    top: 30px;
    text-align: right;
    right: 30px;
    width: 100%;
    max-width: var(--page-container-width, 1260px);
    margin: 0 auto;
    z-index: 99999;
    color: #fff;
    font-size: 25px;
    cursor: pointer;
  }

  &__content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: var(--page-container-width, 1260px);
    margin: 0 auto 20px;
    color: #fff;
    display: flex;
    flex-wrap: wrap;

    > div {
      flex: 1;
    }
  }

  &__title {
    h6 {
      color: #a3abb6;
      letter-spacing: -0.005em;
      font-weight: 700;
      font-size: 24px;
      margin: 0;
    }

    h4 {
      font-size: 35px;
      letter-spacing: -0.005em;
      font-weight: 700;
      margin: 35px 0 13px;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;

    button {
      font-weight: 400;
      font-size: 0.625rem;
      letter-spacing: 0.195em;
      text-transform: uppercase;
      color: #fff;
      appearance: none;
      border-radius: 10px;
      padding: 0 20px;
      transition: 0.3s ease background;
      border: 1px solid #fff;
      background: transparent;
      height: 45px;
      line-height: 45px;
      margin: 5px;
      position: relative;
      cursor: pointer;
      white-space: nowrap;
      align-items: center;

      &:hover {
        background-color: #071d3b;
        border: 1px solid #071d3b;
        color: #fff;
      }

      > * + * {
        margin-left: 10px;
      }
    }

    #{$block}__save {
      background-color: var(--primary-color, hsl(208, 100%, 41%));
      color: #fff;
      padding: 0 20px;
      border-radius: 10px;
      border: 1px solid var(--primary-color, hsl(208, 100%, 41%));
      min-width: 100px;
      font-size: 10px;
      display: flex;
      justify-content: space-between;

      &:hover {
        background-color: #071d3b;
        border: 1px solid #071d3b;
      }
    }
  }
}
</style>

<style scoped>
.download-btn.loading .icon {
  animation: spin infinite linear 1s;
}
.icon {
  font-size: 14px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}
</style>
